import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Member } from '../../types';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { UserMinus, RotateCcw } from 'lucide-react';
import { formatDateOnly } from '../../utils/formatters';

interface PeopleTableProps {
  people: Member[];
  loading: boolean;
  onDelete: (id: string) => void;
  onReactivate?: (id: string) => void;
  getPersonName: (personId?: string) => string;
}

export const PeopleTable: React.FC<PeopleTableProps> = ({
  people,
  loading,
  onDelete,
  onReactivate,
  getPersonName,
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground" data-testid="people-table-loading">
        {t('table.loading')}
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground" data-testid="people-table-empty">
        {t('table.noPeople')}
      </div>
    );
  }

  return (
    <Table data-testid="people-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.headers.name')}</TableHead>
          <TableHead>{t('table.headers.email')}</TableHead>
          <TableHead>{t('table.headers.role')}</TableHead>
          <TableHead>{t('table.headers.reportsTo')}</TableHead>
          <TableHead>{t('table.headers.clients')}</TableHead>
          <TableHead>{t('table.headers.status')}</TableHead>
          <TableHead className="text-right">{tCommon('table.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {people.map((person) => (
          <TableRow key={person.id} data-testid={`people-table-row-${person.id}`}>
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar className="h-10 w-10" data-testid={`people-avatar-${person.id}`}>
                    {(person.profilePictureUrl || person.photoURL) && (
                      <AvatarImage src={person.profilePictureUrl || person.photoURL} alt={person.name} />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {getInitials(person.name)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <button
                  onClick={() => navigate(`/people/${person.id}`)}
                  className="font-medium hover:text-primary hover:underline cursor-pointer text-left"
                  data-testid={`people-link-${person.id}`}
                >
                  {person.name}
                </button>
              </div>
            </TableCell>
            <TableCell>{person.email}</TableCell>
            <TableCell>{person.organizationRole}</TableCell>
            <TableCell>{getPersonName(person.reportsTo?.id)}</TableCell>
            <TableCell>
              {person.clients && person.clients.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {person.clients.slice(0, 2).map((client, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {client}
                    </Badge>
                  ))}
                  {person.clients.length > 2 && (
                    <Badge variant="secondary" className="text-xs">
                      +{person.clients.length - 2}
                    </Badge>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">{tCommon('none')}</span>
              )}
            </TableCell>
            <TableCell>
              <Badge variant={person.isActive !== false ? 'default' : 'secondary'}>
                {person.isActive !== false ? tCommon('status.active') : tCommon('status.inactive')}
              </Badge>
              {person.isActive === false && person.offboardingDate && (
                <div
                  className="mt-1 text-xs text-muted-foreground"
                  data-testid={`people-offboarding-info-${person.id}`}
                >
                  <div>{t('offboarding.leftOn', { date: formatDateOnly(person.offboardingDate) })}</div>
                  {person.offboardingReason && (
                    <div className="truncate max-w-[200px]" title={person.offboardingReason}>
                      {person.offboardingReason}
                    </div>
                  )}
                </div>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                {person.isActive === false ? (
                  onReactivate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onReactivate(person.id)}
                      className="hover:bg-green-50"
                      data-testid={`people-button-reactivate-${person.id}`}
                      title={t('table.reactivate', 'Reactivate')}
                    >
                      <RotateCcw className="h-4 w-4 text-green-600" />
                    </Button>
                  )
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(person.id)}
                    className="hover:bg-destructive/10"
                    data-testid={`people-button-delete-${person.id}`}
                  >
                    <UserMinus className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
